import { useInfiniteQuery, useQuery } from '@tanstack/react-query';
import { useAuthStore } from '@stores/auth';
import {
  notificationsApi,
  type NotificationsListParams,
  type NotificationsListResponse,
  type NotificationType,
} from '../api/notificationsApi';

export interface UseNotificationsListOptions {
  read?: boolean;
  type?: NotificationType;
  /** Page size — defaults to 20. The bell dropdown overrides this to 10. */
  limit?: number;
  enabled?: boolean;
}

/**
 * Paginated list — used by the full Notification Center page. Returns the
 * standard React Query `useInfiniteQuery` shape; consumers flatten
 * `data.pages[*].data` to render rows and call `fetchNextPage()` when the
 * sentinel scrolls into view.
 */
export function useNotificationsInfinite(options: UseNotificationsListOptions = {}) {
  const { read, type, limit = 20, enabled = true } = options;
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated);

  return useInfiniteQuery<NotificationsListResponse>({
    queryKey: ['notifications', 'list', { read, type, limit }],
    queryFn: ({ pageParam = 1 }) =>
      notificationsApi.list({
        page: pageParam as number,
        limit,
        read,
        type,
      } satisfies NotificationsListParams),
    initialPageParam: 1,
    enabled: enabled && isAuthenticated,
    getNextPageParam: (lastPage) => {
      const { page, totalPages } = lastPage.pagination;
      return page < totalPages ? page + 1 : undefined;
    },
    staleTime: 15_000,
  });
}

/**
 * Single-page fetch — used by the bell dropdown (last 10 only).
 * Keying mirrors the infinite variant so the SSE stream's
 * `invalidateQueries(['notifications'])` refreshes both surfaces.
 */
export function useNotificationsList(options: UseNotificationsListOptions = {}) {
  const { read, type, limit = 10, enabled = true } = options;
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated);

  return useQuery<NotificationsListResponse>({
    queryKey: ['notifications', 'list', 'preview', { read, type, limit }],
    queryFn: () =>
      notificationsApi.list({ page: 1, limit, read, type } satisfies NotificationsListParams),
    enabled: enabled && isAuthenticated,
    staleTime: 15_000,
  });
}
